import { readOperationStream, type OperationProgress } from "./operationStream";
import type { components } from "@/generated/core-api";

import { CoreError, type CoreErrorKind } from "./errors";
import { browserGeneration } from "./productGeneration";
import { browserSessionContext, isAccountContextPath } from './sessionContext';

/**
 * The one place that speaks HTTP. Both the session client and the patient
 * client go through it, so cookie handling, error mapping and the same-origin
 * rule are stated once rather than per endpoint.
 */

type WireError = components["schemas"]["ErrorResponse"];

/**
 * Methods the Core contract actually uses.
 *
 * `DELETE` arrived with record-link removal, which is the contract's only one.
 * It carries no body and answers with the attachment detail rather than 204, so
 * it needs nothing from this module beyond being allowed through.
 *
 * `PATCH` arrived with the personal diagnosis dictionary, whose two edits — a
 * folder's name, an entry's folder — each replace ONE property of a resource
 * whose other properties the client does not own. `PUT` would ask the browser
 * to send back a whole shape it never authored.
 *
 * `HEAD` is the tus resume read: the response carries the accepted byte offset
 * in headers and deliberately has no body.
 */
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD";

export const API_ROOT = "/api/v1";
export const SCHEDULE_API_ROOT = "/api/schedule";
const responseContexts = new WeakMap<Response, number>();

/** Contract error codes carry more intent than the status alone. */
const KIND_BY_CODE: Record<WireError["code"], CoreErrorKind> = {
  external_entry_changed: 'conflict',
  external_entry_not_editable: 'conflict',
  external_entry_proposal_decided: 'conflict',
  processing_changed: 'conflict',
  processing_document_busy: 'conflict',
  processing_limit_exceeded: 'invalid-input',
  clinical_summary_language_resources_invalid: 'unavailable',
  clinical_summary_model_profile_unknown: 'invalid-input',
  clinical_summary_changed: 'conflict',
  clinical_summary_no_sources: 'invalid-input',
  clinical_summary_invalid: 'invalid-input',
  clinical_summary_letterhead_changed: 'conflict',
  clinical_summary_letterhead_invalid: 'invalid-input',
  clinical_summary_letterhead_active_content: 'invalid-input',
  clinical_summary_letterhead_unreadable: 'invalid-input',
  cash_register_agent_revoked: 'conflict',
  cash_register_amount_out_of_range: 'conflict',
  cash_register_archived: 'conflict',
  cash_register_cashier_identity_missing: 'conflict',
  cash_register_configuration_changed: 'conflict',
  cash_register_conflicting_evidence: 'conflict',
  cash_register_device_assigned: 'conflict',
  cash_register_device_changed: 'conflict',
  cash_register_document_mismatch: 'conflict',
  cash_register_document_missing: 'conflict',
  cash_register_fiscal_source_incomplete: 'conflict',
  cash_register_installation_bound: 'conflict',
  cash_register_no_effect_requires_agent_evidence: 'conflict',
  cash_register_not_paired: 'conflict',
  cash_register_not_prepared: 'conflict',
  cash_register_not_ready: 'conflict',
  cash_register_not_unknown: 'conflict',
  cash_register_offline: 'conflict',
  cash_register_operation_not_executable: 'conflict',
  cash_register_original_document_missing: 'conflict',
  cash_register_pairing_invalid: 'conflict',
  cash_register_pairing_throttled: 'conflict',
  cash_register_payload_mismatch: 'conflict',
  cash_register_preview_changed: 'conflict',
  cash_register_protocol_incompatible: 'conflict',
  cash_register_refund_source_missing: 'conflict',
  cash_register_result_proof_invalid: 'conflict',
  cash_register_result_sequence: 'conflict',
  cash_register_revision: 'conflict',
  cash_register_shift_evidence_mismatch: 'conflict',
  cash_register_shift_evidence_missing: 'conflict',
  cash_register_shift_not_open: 'conflict',
  cash_register_source_already_fiscalized: 'conflict',
  cash_register_source_scope_mismatch: 'conflict',
  cash_register_too_many_items: 'conflict',
  cash_register_transfer_required: 'conflict',
  cash_register_unresolved: 'conflict',
  cash_register_unsupported_partial_refund: 'conflict',
  cash_register_unsupported_partial_supply: 'conflict',
  cash_register_unsupported_payment_method: 'conflict',
  cash_register_unsupported_profile: 'conflict',
  cash_register_unsupported_refund: 'conflict',
  cash_register_unsupported_tax: 'conflict',
  clinic_maintenance: "unavailable",
  maintenance_request_changed: "conflict",
  maintenance_state_changed: "conflict",
  maintenance_window_exists: "conflict",
  maintenance_window_changed: "conflict",
  maintenance_already_started: "conflict",
 organization_quota_exceeded: "conflict",
  workplace_invalid: 'invalid-input',
  workplace_coding_invalid: 'invalid-input',
  workplace_profile_changed: 'conflict',
  workplace_changed: 'conflict',
  workplace_required: 'conflict',
  workplace_unavailable: 'conflict',
  workplace_branch_unavailable: 'conflict',
  workplace_duplicate: 'conflict',
  workplace_future_bookings: 'conflict',
  organization_not_ready: 'conflict',
  organization_ai_project_unavailable: 'unavailable',
  staff_invitation_invalid: 'invalid-input',
  staff_invitation_idempotency_conflict: 'conflict',
  staff_invitation_workplaces_required: 'conflict',
  staff_invitation_workplace_unavailable: 'conflict',
  staff_invitation_changed: 'conflict',
  staff_invitation_closed: 'conflict',
  staff_invitation_delivery_closed: 'conflict',
  staff_invitation_rate_limited: 'throttled',
  staff_invitation_account_kind: 'conflict',
  staff_invitation_already_member: 'conflict',
  staff_invitation_restore_required: 'conflict',
  staff_invitation_sign_in_required: 'conflict',
  staff_invitation_account_required: 'invalid-input',
  staff_invitation_credentials_required: 'invalid-input',
  staff_invitation_identity_conflict: 'conflict',
  staff_invitation_account_conflict: 'conflict',
  account_password_already_set: 'conflict',
  account_email_cooldown: 'conflict',
  account_email_challenge_missing: 'conflict',
  account_email_expired: 'conflict',
  account_email_code_invalid: 'conflict',
  account_email_identity_conflict: 'conflict',
  organization_setup_idempotency_conflict: 'conflict',
  organization_setup_changed: 'conflict',
  organization_setup_closed: 'conflict',
  langfuse_project_changed: 'conflict',
  langfuse_project_scope_mismatch: 'conflict',
  organization_create_invalid: 'invalid-input',
  organization_create_idempotency_conflict: 'conflict',
  telegram_reauthentication_required: 'invalid-credentials',
  person_name_invalid: "invalid-input",
  person_name_ambiguous: "conflict",
  person_names_changed_concurrently: "conflict",
  person_names_inactive: "conflict",
  patient_curator_changed_concurrently: "conflict",
  patient_curator_ineligible: "conflict",
  patient_curator_cycle: "conflict",
  patient_already_connected: "conflict",
  patient_identity_changed: "conflict",
  product_generation_changed: "conflict",
  session_context_changed: "conflict",
  product_generation_required: "conflict",
  product_generation_not_admitted: "unavailable",
  // The account profile lost the same race a Patient title sheet can lose, and
  // it is reported the same way so the two cards can offer the same recovery.
  account_profile_changed_concurrently: "conflict",
  ai_prompt_file_changed_concurrently: "conflict",
  authorization_role_changed_concurrently: "conflict",
  authorization_role_code_taken: "conflict",
  authorization_role_in_use: "conflict",
  attachment_digest_mismatch: "conflict",
  attachment_metadata_changed_concurrently: "conflict",
  attachment_purge_changed_concurrently: "conflict",
  attachment_size_mismatch: "conflict",
  bad_request: "invalid-input",
  communication_audience_mismatch: "conflict",
  communication_conversation_mismatch: "conflict",
  communication_request_invalid: "invalid-input",
  communication_patient_in_app_forbidden: "invalid-input",
  communication_revision_conflict: "conflict",
  communication_identity_conflict: "conflict",
  communication_idempotency_conflict: "conflict",
  communication_connector_mismatch: "conflict",
  communication_connector_disabled: "conflict",
  communication_route_missing: "conflict",
  communication_route_ambiguous: "conflict",
  communication_route_changed: "conflict",
  communication_preference_denied: "conflict",
  communication_preference_required: "conflict",
  communication_endpoint_unverified: "conflict",
  communication_lease_lost: "conflict",
  communication_no_selected_channels: "conflict",
  communication_endpoint_ambiguous: "conflict",
  communication_payload_unavailable: "unavailable",
  communication_delivery_target_required: "conflict",
  communication_module_unavailable: "unavailable",
  communication_unavailable: "unavailable",
  communication_source_inactive: "conflict",
  connector_invalid: "invalid-input",
  connector_revision_conflict: "conflict",
  connector_duplicate_identity: "conflict",
  connector_purpose_conflict: "conflict",
  connector_identity_mismatch: "conflict",
  connector_not_configured: "conflict",
  connector_migration_unresolved: "conflict",
  // A malformed or impossible birth date is refused before the repository runs.
  // It reaches here as its own code so the screen can say which field was
  // wrong rather than reporting a generic failure the server did not give.
  patient_birth_date_invalid: "invalid-input",
  patient_case_changed_concurrently: "conflict",
  patient_case_date_order_invalid: "invalid-input",
  patient_case_not_active: "conflict",
  patient_case_not_planned: "conflict",
  patient_case_terminal: "conflict",
  patient_deceased: "conflict",
  patient_death_invalid: "invalid-input",
  patient_death_mismatch: "conflict",
  patient_changed_concurrently: "conflict",
  patient_consent_active_exists: "conflict",
  patient_consent_changed: "conflict",
  patient_consent_not_submitted: "conflict",
  patient_consent_request_invalid: "invalid-input",
  // The VIP fact versions independently of the title sheet, so its lost race
  // arrives under its own code and recovery reloads only the VIP state.
  patient_vip_changed_concurrently: "conflict",
  clinical_record_already_corrected: "conflict",
  clinical_service_context_changed: "conflict",
  clinical_service_correction_changed: "conflict",
  clinical_service_units_already_corrected: "conflict",
  finance_responsibility_credit_unavailable: "conflict",
  finance_coverage_unwind_provenance_required: "conflict",
  clinical_record_changed_concurrently: "conflict",
  clinical_record_correction_idempotency_conflict: "conflict",
  clinical_record_definition_unpublished: "conflict",
  clinical_record_effective_definition_conflict: "conflict",
  clinical_record_final_content_immutable: "conflict",
  clinical_record_idempotency_conflict: "conflict",
  clinical_record_not_correctable: "conflict",
  clinical_record_not_preliminary: "conflict",
  clinical_record_not_finalizable: "conflict",
  clinical_record_translation_already_exists: "conflict",
  clinical_record_translation_changed_concurrently: "conflict",
  clinical_record_translation_source_changed: "conflict",
  clinical_dictation_schema_changed: "conflict",
  clinical_dictation_schema_unsupported: "invalid-input",
  diagnosis_context_changed_concurrently: "conflict",
  diagnosis_context_main_required: "conflict",
  diagnosis_context_main_conflict: "conflict",
  diagnosis_context_readonly: "conflict",
  diagnosis_attributes_invalid: "invalid-input",
  diagnosis_coding_invalid: "invalid-input",
  anamnesis_changed_concurrently: "conflict",
  anamnesis_coding_invalid: "invalid-input",
  document_intelligence_provider_changed_concurrently: "conflict",
  // Two folders of one clinician cannot share a name, because the name is how
  // the clinician tells them apart. It is the state of a folder that already
  // exists, so it reads as a conflict and the panel says which name is taken.
  diagnosis_personal_folder_name_conflict: "conflict",
  document_idempotency_conflict: "conflict",
  document_operation_lease_lost: "conflict",
  document_output_conflict: "conflict",
  document_run_canceled: "conflict",
  dicom_uid_collision: "conflict",
  encounter_already_finished: "conflict",
  encounter_changed_concurrently: "conflict",
  encounter_entered_in_error: "conflict",
  encounter_finish_conflict: "conflict",
  encounter_not_finishable: "conflict",
  encounter_parameters_changed: 'conflict',
  encounter_parameter_profile_changed: 'conflict',
  encounter_parameters_readonly: 'conflict',
  encounter_parameters_required: 'conflict',
  patient_emr_entry_idempotency_conflict: "conflict",
  // Registering a Patient: a key already used for a different person, and a
  // clinic whose ten-digit code space is full. The first is reconcilable by
  // the clinician, the second is not, but both are the state of something
  // that already exists rather than a bad request.
  patient_registration_idempotency_conflict: "conflict",
  patient_code_exhausted: "conflict",
  // Sending a message to a Patient. The request refusal is the composer's own
  // bounds restated by the server; the other two are the state of something
  // that already exists — a Patient with no address on file, and a key already
  // spent on a different message — so the composer says which, by name.
  patient_message_request_invalid: "invalid-input",
  patient_message_recipient_missing: "conflict",
  patient_message_idempotency_conflict: "conflict",
  message_request_invalid: "invalid-input",
  message_recipient_missing: "conflict",
  message_idempotency_conflict: "conflict",
  // The module-scope answer for a registry filter naming a module code the
  // clinic cannot see: unregistered, hidden and malformed all answer this one
  // code, so the browser cannot become a hidden-module oracle by comparing.
  unknown_module: "not-found",
  module_hidden: "conflict",
  module_state_changed_concurrently: "conflict",
  clinic_ownership_target_invalid: "invalid-input",
  clinic_settings_changed_concurrently: "conflict",
  clinic_user_changed_concurrently: "conflict",
  clinic_user_has_medical_objects: "conflict",
  legal_entity_changed_concurrently: "conflict",
  legal_entity_tax_duplicate: "conflict",
  legal_entity_lifecycle_conflict: "conflict",
  openai_account_key_missing: "conflict",
  ai_function_disabled: 'conflict',
  laboratory_pipeline_requires_import: 'conflict',
  username_taken: "conflict",
  conflict: "conflict",
  forbidden: "forbidden",
  finance_cost_estimate_changed_concurrently: "conflict",
  finance_cost_estimate_issue_invalid: "conflict",
  finance_cost_estimate_issued: "conflict",
  finance_cost_estimate_limit_exceeded: "invalid-input",
  finance_cost_estimate_source_unavailable: "conflict",
  finance_correction_reason_required: "invalid-input",
  finance_correction_unsupported: "conflict",
  finance_external_cost_catalog_changed_concurrently: "conflict",
  finance_external_cost_code_conflict: "conflict",
  finance_external_cost_cycle: "conflict",
  finance_external_cost_depth_exceeded: "conflict",
  finance_external_cost_parent_unavailable: "conflict",
  finance_idempotency_conflict: "conflict",
  finance_financial_evidence_changed_concurrently: "conflict",
  finance_financial_evidence_incomplete: "conflict",
  finance_not_found: "not-found",
  // The settlement module's money refusals are ValidationError on the server;
  // its account, person, profile and period refusals are ConflictError.
  finance_money_invalid: "invalid-input",
  finance_money_overflow: "invalid-input",
  finance_currency_mismatch: "invalid-input",
  finance_account_changed_concurrently: "conflict",
  finance_account_state_conflict: "conflict",
  finance_account_exists: "conflict",
  finance_person_changed_concurrently: "conflict",
  finance_profile_unconfigured: "conflict",
  finance_profile_changed_concurrently: "conflict",
  finance_profile_currency_locked: "conflict",
  finance_period_closed: "conflict",
  finance_period_changed_concurrently: "conflict",
  finance_period_state_conflict: "conflict",
  // The settlement and dated-catalog refusals published with the module's
  // allocation, refund and pricing routes — every one a ConflictError.
  finance_funds_insufficient: "conflict",
  finance_payment_permission_exists: "conflict",
  finance_funds_restricted: "conflict",
  finance_responsibility_eligibility_conflict: "conflict",
  finance_responsibility_exceeded: "conflict",
  finance_allocation_reversal_exceeded: "conflict",
  finance_refund_exceeds_available: "conflict",
  finance_refund_evidence_already_recorded: "conflict",
  finance_delegation_unavailable: "conflict",
  finance_purchase_changed_concurrently: "conflict",
  finance_purchase_state_conflict: "conflict",
  finance_purchase_value_exceeded: "conflict",
  finance_purchase_units_unavailable: "conflict",
  finance_dated_catalog_archived: "conflict",
  finance_dated_catalog_code_conflict: "conflict",
  finance_dated_binding_unavailable: "conflict",
  finance_dated_tax_unavailable: "conflict",
  finance_service_archived: "conflict",
  finance_price_list_changed_concurrently: "conflict",
  finance_price_not_found: "not-found",
  finance_request_invalid: "invalid-input",
  finance_service_code_invalid: "invalid-input",
  google_document_ai_key_invalid: "invalid-input",
  google_document_ai_settings_invalid: "invalid-input",
  google_signin_client_id_invalid: "invalid-input",
  hospital_stay_changed_concurrently: "conflict",
  hospital_stay_not_admitted: "conflict",
  hospital_stay_not_planned: "conflict",
  hospital_stay_time_order_invalid: "conflict",
  idempotency_conflict: "conflict",
  imaging_import_exists: "conflict",
  imaging_import_not_confirmable: "conflict",
  imaging_import_not_validating: "conflict",
  imaging_export_idempotency_conflict: "conflict",
  imaging_export_not_cancellable: "conflict",
  imaging_export_source_changed: "conflict",
  imaging_export_spatial_order_unavailable: "conflict",
  imaging_lease_lost: "conflict",
  imaging_quota_exceeded: "conflict",
  imaging_secret_storage_unavailable: "unavailable",
  internal_error: "unavailable",
  invalid_archive_source: "invalid-input",
  invalid_confirmation: "invalid-input",
  invalid_credentials: "invalid-credentials",
  password_change_required: "forbidden",
  invalid_idempotency_key: "invalid-input",
  invalid_imaging_export: "invalid-input",
  invalid_imaging_export_cursor: "invalid-input",
  invalid_imaging_export_recipe: "invalid-input",
  invalid_remote_credential: "invalid-input",
  invalid_remote_source: "invalid-input",
  invalid_scope: "invalid-input",
  invalid_source: "invalid-input",
  invalid_source_count: "invalid-input",
  invalid_source_filename: "invalid-input",
  invalid_source_path: "invalid-input",
  invalid_source_type: "invalid-input",
  invalid_study_context: "invalid-input",
  invalid_study_purge: "invalid-input",
  invalid_study_record_link: "invalid-input",
  invalid_study_selection: "invalid-input",
  lead_code_exhausted: "conflict",
  lead_idempotency_conflict: "conflict",
  lead_link_not_rotatable: "conflict",
  laboratory_import_changed_concurrently: "conflict",
  laboratory_import_not_draft: "conflict",
  not_found: "not-found",
  onboarding_already_submitted: "conflict",
  onboarding_expired: "conflict",
  onboarding_revoked: "conflict",
  payload_too_large: "too-large",
  // A release bundle Core will not accept is the submission's fault, not the
  // registry's state, so both bundle refusals read as invalid input.
  print_form_binding_conflict: "conflict",
  print_form_binding_inactive: "conflict",
  print_form_bundle_incompatible: "invalid-input",
  print_form_bundle_invalid: "invalid-input",
  print_form_context_schema_conflict: "conflict",
  print_form_release_conflict: "conflict",
  // The render command's three refusals. Two are about state the caller can
  // reconcile - a key already used for a different render, a context the
  // current release cannot fill - so they are conflicts. A render answer Core
  // refused is not something the screen can correct, so it reads as unavailable
  // rather than inviting a retry that would fail the same way.
  print_form_render_conflict: "conflict",
  print_form_render_context_incomplete: "conflict",
  print_form_render_rejected: "unavailable",
  print_form_route_conflict: "conflict",
  protocol_not_active: "conflict",
  protocol_release_conflict: "conflict",
  protocol_rule_conflict: "conflict",
  protocol_selection_required: "conflict",
  preview_conversion_failed: "unavailable",
  remote_source_private_address: "invalid-input",
  referral_counterparty_archived: "conflict",
  referral_idempotency_conflict: "conflict",
  referral_legal_entity_archived: "conflict",
  acquisition_source_link_conflict: "conflict",
  acquisition_source_link_not_active: "conflict",
  referral_money_agreement_required: "conflict",
  referral_partner_archived: "conflict",
  referral_partner_changed_concurrently: "conflict",
  referral_partner_ineligible: "conflict",
  referral_partner_lifecycle_invalid: "conflict",
  referral_partner_exists: "conflict",
  referral_request_invalid: "invalid-input",
  referral_reward_rule_overlap: "conflict",
  referral_reward_transition_invalid: "conflict",
  referral_rule_changed_concurrently: "conflict",
  revision_conflict: "conflict",
  role_grant_scope_invalid: "invalid-input",
  // A column funnel's free-text criterion the server would not take: too long,
  // or a pattern its engine refuses. The clinician can rewrite it, so it is
  // input rather than a failure — and it names the criterion rather than the
  // registry, because the registry is fine.
  registry_criterion_invalid: "invalid-input",
  schedule_appointment_already_linked: "conflict",
  schedule_appointment_in_future: "conflict",
  schedule_appointment_not_startable: "conflict",
  schedule_start_changed_concurrently: "conflict",
  schedule_start_selected_case_unavailable: "conflict",
  linked_schedule_event_not_closeable: "conflict",
  service_request_already_invalidated: "conflict",
  service_request_changed_concurrently: "conflict",
  service_request_idempotency_conflict: "conflict",
  service_request_not_activatable: "conflict",
  service_request_not_draft: "conflict",
  service_request_source_record_final: "conflict",
  service_request_source_record_unsupported: "conflict",
  service_request_specialty_unsupported: "conflict",
  service_unavailable: "unavailable",
  source_size_mismatch: "invalid-input",
  source_too_large: "too-large",
  study_not_deletable: "conflict",
  study_not_exportable: "conflict",
  study_record_link_conflict: "conflict",
  task_changed_concurrently: "conflict",
  task_context_mismatch: "conflict",
  task_message_incomplete: "invalid-input",
  task_not_active: "conflict",
  task_simulation_idempotency_conflict: "conflict",
  task_simulation_not_configured: "conflict",
  tracker_release_changed: "conflict",
  tracker_release_not_closed: "conflict",
  telegram_already_linked: "conflict",
  telegram_bot_already_configured: "conflict",
  telegram_bot_not_configured: "conflict",
  telegram_bot_token_invalid: "invalid-input",
  // A refused staff Mini App launch is a failed sign-in, whichever of these
  // it was; the code stays on the error for the bridge to explain it.
  telegram_mini_app_account_inactive: "invalid-credentials",
  telegram_mini_app_launch_expired: "invalid-credentials",
  telegram_mini_app_launch_invalid: "invalid-credentials",
  telegram_mini_app_not_linked: "invalid-credentials",
  // No staff bot on the shard: nobody can sign in through the Mini App until
  // the clinic configures one, which is unavailability rather than conflict.
  telegram_mini_app_not_configured: "unavailable",
  terminology_binding_changed_concurrently: "conflict",
  terminology_not_configured: "conflict",
  too_many_login_attempts: "throttled",
  unauthorized: "unauthenticated",
  unsupported_dicom: "conflict",
  unsupported_language: "invalid-input",
  unsupported_media_type: "unsupported-media",
  unsupported_remote_auth: "invalid-input",
  // Linking WhatsApp to a Person: a link that already stands, and a clinic
  // with no enabled sender for this audience yet. Both are the state of the
  // clinic rather than a bad request, and the manager names each.
  whatsapp_already_linked: "conflict",
  whatsapp_sender_not_configured: "conflict",
};

const KIND_BY_STATUS: Readonly<Record<number, CoreErrorKind>> = {
  400: "invalid-input",
  401: "unauthenticated",
  403: "forbidden",
  404: "not-found",
  409: "conflict",
  413: "too-large",
  415: "unsupported-media",
  429: "throttled",
};

export interface RequestOptions {
  /** FormData and Blob bodies are sent as-is; other values are JSON encoded. */
  body?: unknown;
  headers?: Readonly<Record<string, string>>;
  /**
   * ONE WRITE ATTEMPT'S IDENTITY, sent as `Idempotency-Key`.
   *
   * Written here rather than by each caller because idempotency is what makes a
   * retried write safe, and the header spelling was the half of it the tree had
   * copied: twenty-eight call sites across sixteen clients, one of them in
   * double quotes where the rest used single, so a search for the literal
   * missed it. Mint the key with `newIdempotencyKey` and pass it here.
   */
  idempotencyKey?: string;
  /** Keep a bounded request alive while the document is being unloaded. */
  keepalive?: boolean;
  query?: Readonly<
    Record<string, string | number | readonly (string | number)[] | undefined>
  >;
  /** Per-call overrides for statuses whose meaning depends on the endpoint. */
  statusKinds?: Partial<Record<number, CoreErrorKind>>;
  ignoreStatuses?: readonly number[];
  signal?: AbortSignal;
  operationProgress?: (progress: OperationProgress) => void;
}

/**
 * The one HTTP path. Returns the raw Response on success so a caller that needs
 * bytes rather than JSON still shares this function's auth, abort and failure
 * semantics — a second transport is how a 413 ends up meaning two things.
 *
 * Returns null when the status was listed in `ignoreStatuses`.
 */
export async function requestRaw(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<Response | null> {
  const {
    body,
    headers,
    idempotencyKey,
    keepalive,
    query,
    statusKinds,
    ignoreStatuses,
    signal,
    operationProgress,
  } = options;
  const url = query ? `${path}${buildQuery(query)}` : path;
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const isBlob = typeof Blob !== "undefined" && body instanceof Blob;
  const generationHeaders = browserGeneration.headers();
  const accountPath = isAccountContextPath(path, method);
  const switching = path === `${API_ROOT}/auth/clinic`;
  const contextHeaders = browserSessionContext.headers(!accountPath);
  if (switching) browserSessionContext.invalidate();
  const contextEpoch = browserSessionContext.capture();

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      // Same-origin only: nginx proxies `/api/` to the Core API, so the session
      // cookie stays first-party and is never attached to a foreign origin.
      credentials: "same-origin",
      headers: {
        Accept: operationProgress ? "application/x-ndjson" : "application/json",
        // A multipart body must NOT carry a hand-set Content-Type: only the
        // browser knows the boundary it generated.
        ...(body === undefined || isForm || isBlob
          ? {}
          : { "Content-Type": "application/json" }),
        ...(headers ?? {}),
        ...(idempotencyKey === undefined
          ? {}
          : { "Idempotency-Key": idempotencyKey }),
        ...generationHeaders,
        ...contextHeaders,
      },
      ...(body === undefined
        ? {}
        : {
            body: isForm || isBlob ? (body as BodyInit) : JSON.stringify(body),
          }),
      ...(signal ? { signal } : {}),
      ...(keepalive === undefined ? {} : { keepalive }),
    });
  } catch (cause) {
    // An aborted request is the caller changing its mind, not a failure.
    if (signal?.aborted) {
      throw cause;
    }
    throw new CoreError(
      "unavailable",
      `network failure calling ${method} ${url}`,
    );
  }

  browserGeneration.observe(response);
  if (response.ok && operationProgress)
    response = await readOperationStream(response, (progress) => {
      browserSessionContext.assertCurrent(contextEpoch);
      operationProgress(progress);
    });

  if (accountPath || switching) browserSessionContext.assertUnchanged(contextEpoch);
  else browserSessionContext.assertCurrent(contextEpoch);

  if (response.ok) {
    responseContexts.set(response, contextEpoch);
    return response;
  }

  if (ignoreStatuses?.includes(response.status)) {
    return null;
  }

  const bodyText = await response.text().catch(() => "");
  const details = errorDetailsFromBody(bodyText);
  if (details?.code === 'session_context_changed') browserSessionContext.invalidate();
  const kind =
    statusKinds?.[response.status] ?? kindFromFailure(response.status, details);

  if (details?.["code"] === 'organization_quota_exceeded' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('terysling:quota-exceeded'))
  }
  throw new CoreError(
    kind,
    `${method} ${url} -> ${response.status}`,
    response.status,
    (details && asString(details["code"])) || undefined,
    details ?? undefined,
  );
}

export async function readResponseBlob(response: Response): Promise<Blob> {
  const epoch = responseContexts.get(response);
  if (epoch === undefined) throw new CoreError('malformed', 'Response has no organization context');
  browserSessionContext.assertCurrent(epoch);
  const blob = await response.blob();
  browserSessionContext.assertCurrent(epoch);
  return blob;
}

export async function request(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<unknown> {
  const pending = requestRaw(method, path, options);
  const epoch = browserSessionContext.capture();
  const response = await pending;
  if (!response || response.status === 204) {
    return undefined;
  }
  const body = await readJson(response, method, path);
  browserSessionContext.assertUnchanged(epoch);
  return body;
}

function buildQuery(
  query: Readonly<
    Record<string, string | number | readonly (string | number)[] | undefined>
  >,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== "") {
          params.append(key, String(item));
        }
      }
    } else if (value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

async function readJson(
  response: Response,
  method: string,
  path: string,
): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    throw new CoreError(
      "malformed",
      `${method} ${path} returned a non-JSON body`,
      response.status,
    );
  }
}

/** A JSON object error body; endpoint readers validate any structured details. */
function errorDetailsFromBody(
  bodyText: string,
): Record<string, unknown> | null {
  try {
    return asRecord(JSON.parse(bodyText));
  } catch {
    return null;
  }
}

/**
 * Map a failed response to a kind from its status and, when present, its
 * contract error code.
 *
 * It does NOT exist because uploads speak XMLHttpRequest: they do not.
 * `attachmentsClient` says so explicitly and records XHR as a REJECTED
 * alternative — considered for progress reporting, refused as not worth a
 * second HTTP path. A comment describing that rejected design in the present
 * tense cost one reader a detour through a code path that does not exist.
 *
 * NOT exported, after two comments in a row justified an export by a caller
 * that does not exist. The first said uploads speak XMLHttpRequest; the second
 * said an upload surface "classifies an aborted or refused send without going
 * back through `requestRaw`". Neither caller was ever real: every mention in
 * the repository is this definition and the one call in `requestRaw` above it,
 * and all nine client modules import `request`/`requestRaw` without this.
 *
 * The typecheck is what settles it rather than the search — if anything did
 * consume this, dropping the keyword fails the build, and that failure would
 * be the consumer neither comment could name.
 *
 * Keep one vocabulary: two error vocabularies for one API is how a 413 ends up
 * telling someone to try again later.
 */
function kindFromFailure(
  status: number,
  body: Record<string, unknown> | null,
): CoreErrorKind {
  const code = body && asString(body["code"]);
  if (code && code in KIND_BY_CODE) {
    return KIND_BY_CODE[code as WireError["code"]];
  }
  // An uncoded 413 did not come from the API. nginx answers an oversize body
  // with an HTML page, and the limit it applied is not the contract's.
  if (status === 413) {
    return "too-large-unstated";
  }
  return KIND_BY_STATUS[status] ?? "unavailable";
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}
